<template>
  <div class="ss" ref="root">
    <div
      v-if="multiple"
      class="ss-control ss-multi input" role="button" :tabindex="disabled ? -1 : 0" :aria-disabled="disabled"
      @click="toggleOpen" @keydown.enter.prevent="toggleOpen" @keydown.space.prevent="toggleOpen"
    >
      <span v-if="selectedOptions.length" class="ss-chips">
        <span v-for="opt in selectedOptions" :key="opt.value" class="ss-chip" :title="opt.value">
          <span class="ss-chip-label">{{ opt.label }}</span>
          <button
            type="button" class="ss-chip-x" :aria-label="`Remove ${opt.label}`" :disabled="disabled"
            @click.stop="remove(opt.value)"
          >×</button>
        </span>
      </span>
      <span v-else class="ss-placeholder">{{ placeholder }}</span>
      <span class="ss-caret" aria-hidden="true"></span>
    </div>
    <button v-else class="ss-control input" type="button" @click="toggleOpen" :disabled="disabled">
      <span v-if="selectedOption" class="ss-value">
        <AppIcon
          v-if="selectedOption.iconKey"
          class="ss-icon"
          :name="selectedOption.iconKey"
          :color="selectedOption.iconColor"
          :size="iconSize"
        />
        <FavIcon
          v-else-if="selectedOption.domain"
          class="ss-icon"
          :domain="selectedOption.domain"
          :size="iconSize"
        />
        <HeroIcon
          v-else-if="selectedOption.iconName"
          class="ss-icon"
          :name="selectedOption.iconName"
          :iconStyle="selectedOption.iconStyle"
          :color="selectedOption.iconColor"
          :size="iconSize"
          aria-hidden="true"
        />
        <img
          v-else-if="selectedOption.icon"
          :src="selectedOption.icon"
          class="ss-icon"
          :style="{ width: iconSize + 'px', height: iconSize + 'px' }"
          alt=""
          aria-hidden="true"
          loading="lazy"
          draggable="false"
        />
        <span class="ss-label">{{ selectedOption.label }}</span>
        <span v-if="$slots.optionRight || selectedOption.secondaryLabel" class="ss-secondary" :title="selectedOption.secondaryLabel || undefined">
          <slot name="optionRight" :option="selectedOption">{{ selectedOption.secondaryLabel }}</slot>
        </span>
      </span>
      <span v-else class="ss-placeholder">{{ placeholder }}</span>
      <span class="ss-caret" aria-hidden="true"></span>
    </button>

    <div v-if="open" class="ss-pop">
      <div v-if="!hideSearch" class="ss-search">
        <input
          ref="searchInput"
          class="ss-search-input"
          v-model="q"
          :placeholder="searchPlaceholder"
          @keydown.escape.prevent="setOpen(false)"
        />
      </div>

      <div class="ss-list" :style="{ maxHeight: maxHeight + 'px' }">
        <div v-if="atMax" class="ss-empty">You can pick up to {{ max }}.</div>
        <button
          v-if="allowClear && !multiple"
          type="button"
          class="ss-item"
          :class="{ 'ss-item-selected': !modelValue }"
          @click="select('')"
        >
          <span class="ss-label">{{ clearLabel }}</span>
        </button>

        <button
          v-for="opt in visibleOptions"
          :key="String(opt.value)"
          type="button"
          class="ss-item"
          :class="{
            'ss-item-disabled': opt.disabled,
            'ss-item-selected': !opt.disabled && opt.value === modelValue,
          }"
          :disabled="opt.disabled"
          @click="opt.disabled ? null : select(opt.value)"
        >
          <AppIcon
            v-if="opt.iconKey"
            class="ss-icon"
            :name="opt.iconKey"
            :color="opt.iconColor"
            :size="iconSize"
          />
          <FavIcon
            v-else-if="opt.domain"
            class="ss-icon"
            :domain="opt.domain"
            :size="iconSize"
          />
          <HeroIcon
            v-else-if="opt.iconName"
            class="ss-icon"
            :name="opt.iconName"
            :iconStyle="opt.iconStyle"
            :color="opt.iconColor"
            :size="iconSize"
            aria-hidden="true"
          />
          <img
            v-else-if="opt.icon"
            :src="opt.icon"
            class="ss-icon"
            :style="{ width: iconSize + 'px', height: iconSize + 'px' }"
            alt=""
            aria-hidden="true"
            loading="lazy"
            draggable="false"
          />
          <span class="ss-label">{{ opt.label }}</span>
          <span v-if="$slots.optionRight || opt.secondaryLabel" class="ss-secondary" :title="opt.secondaryLabel || undefined">
            <slot name="optionRight" :option="opt">{{ opt.secondaryLabel }}</slot>
          </span>
        </button>

        <div v-if="optionsFunction && asyncLoading" class="ss-empty">Searching…</div>
        <div v-else-if="visibleOptions.length === 0 && !atMax" class="ss-empty">No options</div>
        <div v-else-if="truncated" class="ss-trunc">Type to search to narrow results…</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import FavIcon from './FavIcon.vue'
import HeroIcon from './HeroIcon.vue'

const props = defineProps({
  // choose one of the following modes to supply data
  options: { type: Array, default: () => [] },
  optionsFunction: { type: Function, default: null },  // (query: string) => Promise<Option[]>

  // remaining options apply to both modes
  // A string, or with `multiple` an array of values (shown as chips, in order).
  modelValue: { type: [String, Array], default: '' },
  multiple: { type: Boolean, default: false },
  max: { type: Number, default: Infinity },
  placeholder: { type: String, default: 'Select…' },
  searchPlaceholder: { type: String, default: 'Search…' },
  disabled: { type: Boolean, default: false },
  hideSearch: { type: Boolean, default: false },
  allowClear: { type: Boolean, default: true },
  clearLabel: { type: String, default: '(none)' },
  maxHeight: { type: Number, default: 260 },
  maxResults: { type: Number, default: 200 },
  iconSize: { type: Number, default: 14 },
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const q = ref('')
const root = ref(null)
const searchInput = ref(null)

// ── Async search state ─────────────────────────────────────────
const asyncResults = ref([])
const asyncLoading = ref(false)
const lastSelectedOption = ref(null)
let _asyncTimer = null

function fetchOptions(query) {
  if (_asyncTimer) clearTimeout(_asyncTimer)
  asyncLoading.value = true
  _asyncTimer = setTimeout(async () => {
    try {
      const results = await props.optionsFunction(query)
      if ((q.value || '').trim() === query) {
        asyncResults.value = Array.isArray(results) ? results : []
      }
    } catch {
      asyncResults.value = []
    } finally {
      if ((q.value || '').trim() === query) asyncLoading.value = false
    }
  }, query ? 250 : 0)
}

watch(q, (val) => {
  if (!props.optionsFunction) return
  fetchOptions((val || '').trim())
})

const normalizedOptions = computed(() => {
  const raw = Array.isArray(props.options) ? props.options : []
  return raw
    .map((o) => {
      const rr = o && typeof o === 'object' ? o : {}
      return {
        value: String(rr.value || ''),
        label: String(rr.label || ''),
        secondaryLabel: rr.secondaryLabel ? String(rr.secondaryLabel) : null,
        iconKey: rr.iconKey || null,
        domain: rr.domain || null,
        icon: rr.icon || null,
        iconName: rr.iconName || null,
        iconStyle: rr.iconStyle || 'outline',
        iconColor: rr.iconColor || null,
        disabled: !!rr.disabled,
      }
    })
    .filter((o) => o.value && o.label)
})

function fallbackOption(value) {
  return {
    value,
    label: value,
    secondaryLabel: null,
    iconKey: null,
    domain: null,
    icon: null,
    iconName: null,
    iconStyle: 'outline',
    iconColor: null,
  }
}

const selectedValues = computed(() =>
  props.multiple && Array.isArray(props.modelValue) ? props.modelValue.map((v) => String(v)) : [],
)
const selectedOptions = computed(() =>
  selectedValues.value.map((v) => normalizedOptions.value.find((o) => o.value === v) || fallbackOption(v)),
)
const atMax = computed(() => props.multiple && selectedValues.value.length >= props.max)

const selectedOption = computed(() => {
  if (props.multiple) return null
  const target = String(props.modelValue || '').trim()
  if (!target) return null
  const found = normalizedOptions.value.find((o) => o.value === target)
  if (found) return found
  // In async mode, use the internally-tracked option from last selection
  const last = lastSelectedOption.value
  if (last && last.value === target) return last
  return fallbackOption(target)
})

const filteredOptions = computed(() => {
  if (props.optionsFunction) {
    return asyncResults.value
      .map((o) => {
        const rr = o && typeof o === 'object' ? o : {}
        return {
          value: String(rr.value || ''),
          label: String(rr.label || ''),
          secondaryLabel: rr.secondaryLabel ? String(rr.secondaryLabel) : null,
          iconKey: rr.iconKey || null,
          domain: rr.domain || null,
          icon: rr.icon || null,
          iconName: rr.iconName || null,
          iconStyle: rr.iconStyle || 'outline',
          iconColor: rr.iconColor || null,
          disabled: !!rr.disabled,
        }
      })
      .filter((o) => o.value && o.label)
  }
  const query = (q.value || '').trim().toLowerCase()
  if (!query) return normalizedOptions.value
  // Match the secondary text too.
  return normalizedOptions.value.filter(
    (o) =>
      o.label.toLowerCase().includes(query) ||
      (o.secondaryLabel || '').toLowerCase().includes(query),
  )
})

const truncated = computed(() => filteredOptions.value.length >= props.maxResults)

const visibleOptions = computed(() => {
  if (atMax.value) return []
  const chosen = new Set(selectedValues.value)
  const rows = props.multiple ? filteredOptions.value.filter((o) => !chosen.has(o.value)) : filteredOptions.value
  if (truncated.value) return rows.slice(0, props.maxResults)
  return rows
})

function setOpen(next) {
  open.value = !!next
  if (open.value) {
    nextTick(() => searchInput.value?.focus?.())
    // Refresh async results on EVERY open.
    if (props.optionsFunction) {
      fetchOptions('')
    }
  } else {
    q.value = ''
  }
}

function toggleOpen() {
  if (props.disabled) return
  setOpen(!open.value)
}

function select(v) {
  const val = String(v || '')
  if (props.multiple) {
    // Adds and stays open, so several fields can be picked in a row.
    if (val && !selectedValues.value.includes(val) && !atMax.value) {
      emit('update:modelValue', [...selectedValues.value, val])
    }
    q.value = ''
    nextTick(() => searchInput.value?.focus?.())
    return
  }
  // Track the selected option so we can display it after async results are gone
  lastSelectedOption.value = visibleOptions.value.find((o) => o.value === val) || null
  emit('update:modelValue', val)
  setOpen(false)
}

function remove(v) {
  emit('update:modelValue', selectedValues.value.filter((x) => x !== v))
}

function onDocClick(e) {
  if (!root.value) return
  if (!root.value.contains(e.target)) {
    setOpen(false)
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  if (_asyncTimer) clearTimeout(_asyncTimer)
})
</script>

<style scoped>
.ss {
  position: relative;
  width: 100%;
}

.ss-control {
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--field-bg);
}

.ss-control.ss-multi {
  height: auto;
  min-height: 36px;
  padding-top: 5px;
  padding-bottom: 5px;
}

.ss-chips {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ss-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
  padding: 2px 2px 2px 8px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--badge-bg);
  color: var(--text);
  font-size: 12px;
}

.ss-chip-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ss-chip-x {
  border: 0;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 5px;
}

.ss-chip-x:hover:not(:disabled) {
  color: var(--text);
}

.ss-control:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.ss-value {
  color: var(--text);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.ss-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.ss-secondary {
  flex: 0 1 auto;
  min-width: 0;
  /* A long API name gives way before the label does. */
  max-width: 45%;
  margin-left: auto;
  padding-left: 8px;
  font-size: 11px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ss-placeholder {
  color: var(--muted);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ss-caret {
  flex: 0 0 auto;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid var(--muted);
  opacity: 0.9;
}

.ss-pop {
  position: absolute;
  z-index: 50;
  left: 0;
  right: 0;
  margin-top: 6px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 10px 25px var(--shadow);
  overflow: hidden;
}

.ss-search {
  padding: 8px;
  border-bottom: 1px solid var(--border);
}

.ss-search-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  background: var(--field-bg);
  color: var(--text);
}

.ss-list {
  overflow: auto;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ss-item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--text);
}

.ss-icon {
  flex-shrink: 0;
  border-radius: 2px;
}

.ss-item:hover:not(.ss-item-disabled) {
  background: var(--row-hover-bg);
}

.ss-item-selected,
.ss-item-selected:hover {
  background: var(--nav-active-bg);
  color: var(--accent);
}

.ss-item-disabled {
  color: var(--muted);
  cursor: not-allowed;
  opacity: 0.55;
}

.ss-item-disabled:hover {
  background: transparent;
}

.ss-empty {
  padding: 10px;
  color: var(--muted);
  font-size: 13px;
}

.ss-trunc {
  padding: 10px;
  color: var(--muted);
  font-size: 13px;
  border-top: 1px solid var(--border2);
}
</style>
